import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  useTheme,
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Grid,
  Container,
  Divider,
  Toolbar,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import FormTextField from "../components/form/FormTextField";
import Backdrop from "../components/common/Backdrop";
import { useLoginMutation } from "../features/api/authApiSlice";
import { setCredentials } from "../features/slices/authSlice";
import { selectCurrentToken } from "../features/slices/authSlice";
import { setAlert } from "../features/slices/alertSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import Image from "../components/common/Image";
import { useTranslation } from "react-i18next";
import ImageCarousel from "../components/common/ImageCarousel";
import * as Constant from "../features/constant";
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
  useAddFingerprintMutation,
} from "../features/api/publicApiSlice";
import { lighten, darken, alpha } from "@material-ui/core";
import FormPhoneNumber from "../components/form/FormPhoneNumber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = useSelector(selectCurrentToken);
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const lang = localStorage.getItem("language");
  const loginLight = lighten(theme.palette.button.login, 0.1);
  const loginDark = darken(theme.palette.button.login, 0.05);
  const [loginType, setLoginType] = useState("username"); 

  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
    navigate({
        pathname: location.pathname,
        search: "", // remove query string
      }, { replace: true }); // replace so it doesn’t add to browser history
  }

  // const bonusSliderImage_en = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  // const bonusSliderImage_bd = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  const BONUS_SLIDER_BASE = process.env.REACT_APP_BONUS_SLIDER || "";

  function getBonusSliderImages(envVar) {
    if (!envVar) return [];
    return envVar.split(",").map(function (fileName) {
      return BONUS_SLIDER_BASE + fileName.trim();
    });
  }

  const bonusSliderImage_en = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_EN);
  const bonusSliderImage_bd = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_BD);

  const loginSchema = yup.object().shape({
    username: yup.string().when([], {
      is: () => loginType === "username",
      then: (schema) => schema.required(t("validation.username")),
    }),
    mobile: yup.string().when([], {
      is: () => loginType === "mobile",
      then: (schema) => schema.required(t("validation.mobilenum")),
    }),
    email: yup.string().when([], {
      is: () => loginType === "email",
      then: (schema) => schema.required(t("validation.emailisrequired")),
    }),
    password: yup.string().required(t("validation.password")),
  });

  // utilize the other hooks to display loader, display error message or anything
  const [login, { data, isLoading, isError, isSuccess, error, reset }] =
    useLoginMutation();

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();

    return result;
  };

  const [addFingerprint, { fingerprintData }] =
    useAddFingerprintMutation();


  useEffect(() => {
    if (token) {
      navigate(Constant.getPath("/"));
    }
  }, []);

  const submitForm = async (data) => {
    let formData = new FormData();
    if (loginType === "username")
      formData.append("username", data.username);
    else if (loginType === "mobile"){
      const finalMobileNumber =
      data?.mobile?.replace(/[^A-Z0-9]+/gi, "");
      formData.append("username", finalMobileNumber);
    }
    else if (loginType === "email")
      formData.append("username", data.email);
    formData.append("password", data.password);
    formData.append("grant_type", "password");
    formData.append("login_type", loginType);

    const userData = await login(formData).unwrap();
    dispatch(setCredentials({ ...userData }));

    const fingerprint = await getFingerprint();

    await addFingerprint({
      fingerprint: fingerprint.visitorId,
      userAgent: navigator.userAgent,
    });
  };

  if (!isLoading && isSuccess) {

    window.loginListener = window.loginListener || window;

    dispatch(
      setAlert({
        isOpen: true,
        message: t("header.loginsuccessmessage"),
        variant: "success",
      })
    );
    navigate(Constant.getPath("/"));
    reset();

    const loginCompleteMessage = { event: 'af_login' };
    window.loginListener.postMessage(JSON.stringify(loginCompleteMessage));
  }

  if (!isLoading && isError) {
    dispatch(
      setAlert({
        isOpen: true,
        message: error.data.error_description,
        variant: "error",
      })
    );
    reset();
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case "mobile":
        return t("mobileloginregister.mobilenumber"); 
      case "username":
        return t("mobileloginregister.username");
      case "email":
        return t("mobileloginregister.email");
      default:
        return type;
    }
  };
  const loginTypes = [
    "username",
    "mobile",
    ...(process.env.REACT_APP_ENABLE_EMAIL_LOGIN ? ["email"] : []),
  ];

  const typeIconMap = {
    username: <PersonOutlineIcon fontSize="small" sx={{ color: "inherit" }}/>,
    mobile: <PhoneIphoneOutlinedIcon fontSize="small" sx={{ color: "inherit" }}/>,
    email: <EmailOutlinedIcon fontSize="small" sx={{ color: "inherit" }}/>,
  };

  const gradient1 = process.env.REACT_APP_COLOR_HEADER_GRADIENT1?.trim();
  const gradient2 = process.env.REACT_APP_COLOR_HEADER_GRADIENT2?.trim();
  const headerImage = process.env.REACT_APP_MOBILE_HEADER_IMAGE?.trim();
  let headerColor;
  if (gradient1 && gradient2) {
    headerColor = `linear-gradient(to right, ${gradient1}, ${gradient2})`;
  } else if (headerImage) {
    headerColor = `url(${headerImage})`;
  } else {
    headerColor = theme.palette.header;
  }

  const LoginContent = (
    <Box
      sx={{
        backgroundColor:
          process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY ||
          theme.palette.background.paper,
        minHeight: mobileView ? "100vh" : "auto",
        width: mobileView ? "100%" : 500, // ✅ fixed width for modal
        borderRadius: mobileView ? 0 : '10px',
        p: mobileView ? 0 : 0,
        // display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        mx: "auto",
        boxShadow: mobileView ? "none" : theme.shadows[5],
        position: "relative",
      }}
    >
        <Toolbar
        sx={{
          background: headerColor,
          borderRadius: mobileView ? 0 : "10px 10px 0 0",
          px: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            flexWrap: "nowrap",          // ✅ prevent wrapping
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,              // ✅ prevent logo shrinking weirdly
            }}
          >
            <img
              src={process.env.REACT_APP_MOBILE_HEADER_LOGO}
              onClick={() => navigate(Constant.getPath("/"))}
              alt={Constant.ALT_TAG_LOGO}
              style={{
                cursor: "pointer",
                maxWidth: "120px",
                height: "auto",
              }}
            />
          </Box>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Signup button */}
          <Button
            variant="outlined"
            disableRipple
            sx={{
              mr: 1,
              whiteSpace: "nowrap",       // ✅ keep text in one line
              borderRadius: "3px",
              color: process.env.REACT_APP_COLOR_PRIMARY,
            }}
            onClick={() =>
              navigate({
                pathname: location.pathname,
                search: "?register=1",
              })
            }
          >
            {t("home.signup")}
          </Button>

          {/* Close button */}
          <IconButton
            onClick={handleClose}
            sx={{
              flexShrink: 0,              // ✅ never wrap
            }}
          >
            <Close sx={{ color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || process.env.REACT_APP_COLOR_TEXT }} />
          </IconButton>
        </Box>
      </Toolbar>

    <Divider></Divider>
    <Box sx ={{p: !mobileView ? 3 : 1}}>
      {mobileView &&
        <ImageCarousel
          banners={lang === "en" ? bonusSliderImage_en : bonusSliderImage_bd}
          navigation={false}
          spaceBetween={0}
        />
      }
  
      <Typography 
      variant="h6"
        sx={{
          textAlign: 'center',
          marginTop: !mobileView ? 0 : 2,
          marginBottom: 2,
          fontWeight: 'bold',
          color: process.env.REACT_APP_COLOR_PRIMARY,
          textTransform: 'uppercase',
        }}>
        {t("mobileloginregister.login")}
      </Typography>

      <form onSubmit={handleSubmit(submitForm)}>
          {/* Tabs */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
          {loginTypes.map((type) => {
            const active = loginType === type;

            return (
              <Box
                key={type}
                onClick={() => setLoginType(type)}
                sx={{
                  mx: 2,
                  pb: 0.5,
                  cursor: "pointer",
                  borderBottom: active
                    ? `2px solid ${theme.palette.primary.main}`
                    : "2px solid transparent",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.75,
                    minHeight: 32,
                    color: active
                      ? theme.palette.primary.main
                      : process.env.REACT_APP_COLOR_TEXT || "white",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      opacity: active ? 1 : 0.7, // keeps inactive calm
                    }}
                  >
                    {typeIconMap[type]}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active ? 600 : 400,
                      textTransform: "capitalize",
                    }}
                  >
                    {getTypeLabel(type)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            backgroundColor: process.env.REACT_APP_IS_LIGHT_THEME === 'true' 
            ? 'rgba(0, 0, 0, 0.05)'  // darken for light theme
            : 'rgba(255, 255, 255, 0.05)', // lighten for dark theme
            borderRadius: 2, // rounded corners
            px: 2,
            marginX: 2,
            marginTop: 2,
            paddingBottom: 3,
            paddingTop: 1,
            backdropFilter: 'blur(6px)',
            border: process.env.REACT_APP_IS_LIGHT_THEME === 'true' ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.15)' ,
            // boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
            borderRadius: '10px',
            
          }}
        >

          {/* Dynamic Input Field according to Login Type */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || undefined,
                mt: 2,
                mr: 1,
              }}
            />
            <Box flex={1}>
              {loginType === "username" && (
                <FormTextField
                  label={t("mobileloginregister.username")}
                  // placeholder="Enter Your Username"
                  control={control}
                  controllerName="username"
                  size="small"
                />
              )}
              {loginType === "mobile" && (
                <FormPhoneNumber
                  label={t("mobileloginregister.mobilenumber")}
                  // placeholder="Enter Your Mobile Number"
                  control={control}
                  controllerName="mobile"
                  size="small"
                  sx={{marginTop: -1000}}
                />
              )}
              {loginType === "email" && (
                <FormTextField
                  label={t("mobileloginregister.email")}
                  // placeholder="Enter Your Email"
                  control={control}
                  controllerName="email"
                  type="email"
                  size="small"
                />
              )}
            </Box>
          </Box>
  
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <KeyIcon
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || undefined,
                mt: 2,
                mr: 1,
              }}
            />
            <Box flex={1}>
              <FormTextField
                label={t("mobileloginregister.password")}
                control={control}
                controllerName="password"
                type="password"
                showPasswordToggle
                size="small"
                // sx={{
                //   backgroundColor: process.env.REACT_APP_COLOR_PROFILE_BACKGROUND
                // }}
              />
            </Box>
          </Box>
          </Box>
          <Box display="flex" justifyContent="center" mt={isError ? "10px" : "30px"}>
            <Button variant="contained" type="submit"
            sx={{
              // color: process.env.REACT_APP_COLOR_TEXT,
              background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
              // fontWeight: 'bold',
              px: 4,
              py: 1,
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              },
            }}
            >
              {t("mobileloginregister.login")}
            </Button>
          </Box>
  
          <Box display="flex" justifyContent="center" alignItems="center">
            <a style={{ color: theme.palette.primary.main }} href="/forgotPassword">
              <Typography mt={2} fontSize={14}>
                {t("mobileloginregister.forgotpassword")}
              </Typography>
            </a>
          </Box>
  
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography
              mt={2}
              fontSize={14}
              sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
            >
              {t("mobileloginregister.contactus")}
              <a style={{ color: theme.palette.primary.main }} href="/ContactUs">
                {t("mobileloginregister.customerservice")}
              </a>
              {"."}
            </Typography>
          </Box>
        </form>
        </Box>
    </Box>
  );

  return (
    <Modal open={open} onClose={handleClose} 
    sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
    }}
      >
        {LoginContent}
    </Modal>
  );
};

export default Login;
